import { useCallback, useEffect, useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFieldArray } from '@hookform/lenses/rhf';
import { Big } from 'big.js';
import { type GlobalError, get, useFormContext, useFormState } from 'react-hook-form';
import { match } from 'ts-pattern';
import { useIxcRegistrationV1ProfileQueryServiceProfileGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-profile-query-service-profile-get';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { DropdownMenu } from '@interxfi/design-system/components/dropdown-menu/dropdown-menu';
import { FormHint } from '@interxfi/design-system/components/form/hint';
import { NumberFormat } from '@interxfi/design-system/components/format/number';
import type { CellContext } from '@interxfi/design-system/components/table';
import { Table } from '@interxfi/design-system/components/table/table';
import type { TableColumnDef } from '@interxfi/design-system/components/table/util/column';
import { getInteractionCell } from '@interxfi/design-system/components/table/util/row-action/row-action';
import { Typography } from '@interxfi/design-system/components/typography';
import DeleteIcon from '@interxfi/design-system/icons/react/icon-delete';
import EditIcon from '@interxfi/design-system/icons/react/icon-edit';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import { parseNumber } from '@interxfi/utilities/number/parse';
import { getYesNoOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { getLinkedCompactIDCell } from '../../../../../../components/table/compact-id-cell';
import { getProfileName } from '../../../profile-utils';
import { getRegistrationProfileDetailPath } from '../../../registration-profile-routing';
import { AddControlPersonFormDrawer } from './add-control-person-drawer';
import { ConfirmRemoveControlPersonAlert } from './confirm-remove-control-person-alert';
import type { ControlPersonFormValues } from './control-person-form';
import { EditControlPersonFormDrawer } from './edit-control-person-drawer';

// The must be at least one officer or director
export function validateControlPersons(controlPersons: ControlPersonFormValues[]) {
  return Boolean(controlPersons.find((person) => person.kind === 'director' || person.kind === 'officer'));
}

function getProfileIdFromControlPersonFormValues(value: ControlPersonFormValues): string | undefined {
  return match(value)
    .with({ kind: 'directBeneficialOwner' }, (r) => r.value.profileId)
    .with({ kind: 'indirectBeneficialOwner' }, (r) => r.value.profileId)
    .with({ kind: 'director' }, (r) => r.value.profileId)
    .with({ kind: 'officer' }, (r) => r.value.profileId)
    .with({ kind: 'other' }, (r) => r.value.profileId)
    .exhaustive(() => undefined);
}

function NameColumn<TData, TValue>({ getValue }: CellContext<TData, TValue>) {
  const profileId = getValue<string | undefined>();
  const { data } = useIxcRegistrationV1ProfileQueryServiceProfileGet(profileId ? { profileId } : undefined);
  const name = getProfileName(data?.profile);

  return name ? <span>{name}</span> : null;
}

function getColumns(
  programId: string | undefined,
  handleRemove: (index: number) => void,
  handleUpdate: (index: number, values: ControlPersonFormValues) => void,
): TableColumnDef<ControlPersonFormValues & { index: number }>[] {
  return [
    {
      id: 'name',
      size: 144,
      minSize: 144,
      header: 'Name',
      accessorFn: (row) => getProfileIdFromControlPersonFormValues(row),
      cell: NameColumn,
    },
    {
      id: 'profileId',
      size: 97,
      minSize: 97,
      align: 'right',
      header: 'Profile ID',
      accessorFn: (row) => getProfileIdFromControlPersonFormValues(row),
      cell: getLinkedCompactIDCell((_, value) => getRegistrationProfileDetailPath(value)),
    },
    {
      id: 'type',
      size: 182,
      minSize: 182,
      maxSize: 182,
      header: 'Type',
      accessorFn: (row) =>
        match(row)
          .with({ kind: 'directBeneficialOwner' }, () => 'Direct Beneficial Owner')
          .with({ kind: 'indirectBeneficialOwner' }, () => 'Indirect Beneficial Owner')
          .with({ kind: 'director' }, () => 'Director')
          .with({ kind: 'officer' }, () => 'Officer')
          .with({ kind: 'other' }, () => 'Other')
          .exhaustive(() => undefined),
    },
    {
      id: 'jobTitle',
      size: 144,
      minSize: 144,
      header: 'Job Title',
      accessorFn: (row) =>
        match(row)
          .with({ kind: 'officer' }, (r) => r.value.title)
          .with({ kind: 'director' }, (r) => r.value.title)
          .with({ kind: 'other' }, (r) => r.value.title)
          .otherwise(() => undefined),
    },
    {
      id: 'signer',
      size: 112,
      minSize: 112,
      maxSize: 112,
      header: 'Signer',
      accessorFn: (row) =>
        match(row)
          .with({ kind: 'directBeneficialOwner' }, (r) => getYesNoOrUndefinedFromNullable(r.value, 'signer'))
          .with({ kind: 'indirectBeneficialOwner' }, (r) => getYesNoOrUndefinedFromNullable(r.value, 'signer'))
          .with({ kind: 'officer' }, (r) => getYesNoOrUndefinedFromNullable(r.value, 'signer'))
          .with({ kind: 'director' }, (r) => getYesNoOrUndefinedFromNullable(r.value, 'signer'))
          .with({ kind: 'other' }, (r) => getYesNoOrUndefinedFromNullable(r.value, 'signer'))
          .exhaustive(() => 'No'),
    },
    {
      id: 'primaryContact',
      size: 112,
      minSize: 112,
      maxSize: 112,
      header: 'Primary Contact',
      accessorFn: (row) =>
        match(row)
          .with({ kind: 'directBeneficialOwner' }, (r) => getYesNoOrUndefinedFromNullable(r, 'primaryContact'))
          .with({ kind: 'indirectBeneficialOwner' }, (r) => getYesNoOrUndefinedFromNullable(r, 'primaryContact'))
          .with({ kind: 'officer' }, (r) => getYesNoOrUndefinedFromNullable(r, 'primaryContact'))
          .with({ kind: 'director' }, (r) => getYesNoOrUndefinedFromNullable(r, 'primaryContact'))
          .with({ kind: 'other' }, (r) => getYesNoOrUndefinedFromNullable(r, 'primaryContact'))
          .exhaustive(() => 'No'),
    },
    {
      id: 'ownership',
      align: 'right',
      size: 77,
      minSize: 77,
      maxSize: 77,
      header: 'Ownership',
      accessorFn: (row) => {
        const rawValue = match(row)
          .with({ kind: 'directBeneficialOwner' }, (r) => r.value.ownership)
          .with({ kind: 'indirectBeneficialOwner' }, (r) => r.value.ownership)
          .otherwise(() => undefined);

        if (rawValue === undefined || rawValue === '') {
          return undefined;
        }

        try {
          return new Big(parseNumber(rawValue)).div(100).toNumber();
        } catch {
          return undefined;
        }
      },
      cell: ({ getValue }) => {
        const value = getValue<number | undefined>();
        if (value === undefined) {
          return null;
        }

        return <NumberFormat formatStyle="percent" value={value} />;
      },
    },
    getInteractionCell((row) => {
      if (!row) {
        return undefined;
      }

      return {
        type: 'dropdownMenu',
        dropdownMenu: {
          Component: DropdownMenu,
          props: {
            items: [
              {
                id: 'edit',
                label: 'Edit',
                icon: <EditIcon aria-hidden />,
                interaction: {
                  type: 'drawer',
                  drawer: {
                    Component: EditControlPersonFormDrawer,
                    props: {
                      programId,
                      handleSubmit: (values: ControlPersonFormValues) => {
                        handleUpdate(row.index, values);
                      },
                      initialValues: row,
                    },
                  },
                },
              },
              {
                id: 'remove',
                label: 'Remove',
                tone: 'danger-dk',
                icon: <DeleteIcon aria-hidden />,
                interaction: {
                  type: 'modal',
                  modal: {
                    Component: ConfirmRemoveControlPersonAlert,
                    props: {
                      handleRemoveControlPerson: () => handleRemove(row.index),
                    },
                  },
                },
              },
            ],
          },
        },
      };
    }),
  ];
}

interface ControlPersonsTableFormProps {
  lens: Lens<ControlPersonFormValues[]>;
  programId: string | undefined;
}

export function ControlPersonsTableForm({ lens, programId }: ControlPersonsTableFormProps) {
  const interop = lens.interop();
  const { clearErrors, trigger } = useFormContext();
  const { append, fields, remove, update } = useFieldArray(interop);
  const data = fields.map((field, index) => ({ ...field, index }));
  const handleUpdate = useCallback(
    (index: number, values: ControlPersonFormValues) => {
      // If setting primary contact, unset all others
      if (values.primaryContact) {
        fields.forEach((field, i) => {
          if (i !== index && field.primaryContact) {
            update(i, { ...field, primaryContact: false });
          }
        });
      }

      update(index, values);
    },
    [update, fields],
  );

  const columns = useMemo(() => getColumns(programId, remove, handleUpdate), [programId, remove, handleUpdate]);

  const { errors } = useFormState({ ...interop, exact: true });
  const fieldError = get(errors, interop.name) as GlobalError | undefined;

  // Clear errors and trigger re-validation if field becomes valid
  useEffect(() => {
    if (fieldError && validateControlPersons(fields)) {
      clearErrors(interop.name);
      trigger();
    }
  }, [fieldError, fields, interop.name, clearErrors, trigger]);

  return (
    <VerticalStack gap={6}>
      {fieldError ? <FormHint variant="error">{fieldError.message}</FormHint> : null}

      <HorizontalStack blockAlign="center" gap={4} wrap="nowrap">
        <AddControlPersonFormDrawer
          handleSubmit={append}
          programId={programId}
          trigger={
            <Button affixIcon={<PlusIcon aria-hidden />} size="medium" variant="light">
              Add a Control Person
            </Button>
          }
        />

        <Typography lineHeight="1rem" size="0.875rem" wrap="break-word">
          (at least one officer or director is required)
        </Typography>
      </HorizontalStack>

      <Table columns={columns} data={data} renderWhenEmpty="No Control Persons" renderWhenNoFiltersMatch="No Control Persons" />
    </VerticalStack>
  );
}
